import isEmpty from 'lodash/isEmpty';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { js_beautify } from 'js-beautify';
import { Box, Fab, IconButton, Tooltip, useTheme } from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import AddIcon from '@mui/icons-material/Add';
import { AlertDialog } from '@tymely/components';
import { ArgMetadatadInUseDetails, IArgumentMetadata, IOrganization, UiId } from '@tymely/atoms';
import {
    useArgumentsMetadataQuery,
    useCreateArgumentMetadataMutation,
    useDeleteArgumentMetadataMutation,
    useDocumentTitle,
    useEditArgumentMetadataMutation,
    useOrganizationsQuery,
    usePoliciesUsageQuery,
    useUser,
} from '@tymely/services';
import { PartialBy } from '@global/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '@tymely/config';
import { useAtom } from 'jotai';
import { argMetadataColumnSizingAtom } from '@tymely/atoms/ui.atoms';
import HighlightedTooltip from '@tymely/features/ArgMetadataEditor/components/HighlightedTooltip';

import { ArgUsage } from './ArgUsage';
import ArgMetadataEditDialog from './ArgMetadataEditDialog';

type UiIArgumentMetadata = UiId<PartialBy<IArgumentMetadata, 'id'>>;

export const ArgumentMetadataEditor = memo(() => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams({});
    const editedArgName = queryParams.get('edit');

    const [argBeingEdited, setArgBeingEdited] = useState<Partial<IArgumentMetadata>>();

    const createArgMetadataMutation = useCreateArgumentMetadataMutation();
    const editArgMetadataMutation = useEditArgumentMetadataMutation();
    const { data: argsMetadata, isFetching } = useArgumentsMetadataQuery();
    const deleteArgMetadata = useDeleteArgumentMetadataMutation();
    const { data: allOrgs = [] } = useOrganizationsQuery();

    useDocumentTitle('Argument Editor');

    const editArgMetadata = (argName: IArgumentMetadata['name'] | undefined) => {
        if (argName) {
            navigate(`${routes.argumentMetadataView}?edit=${argName}`);
        } else {
            navigate(routes.argumentMetadataView);
            setTimeout(() => setArgBeingEdited(undefined));
        }
    };

    useEffect(() => {
        if (editedArgName) {
            setArgBeingEdited(argsMetadata?.find((md) => md.name === editedArgName));
        }
    }, [editedArgName, argsMetadata]);

    const onNewArg = useCallback(() => {
        setArgBeingEdited({
            is_list: false,
            dtype: 'str',
            arg_type: 'TEXT_ARGUMENT',
            is_ticket_arg: false,
            params: {},
            additional_data: {},
        });
    }, []);

    const argsByOrgs = useRef<Map<IOrganization['id'], Set<IArgumentMetadata['name']>>>(new Map());
    const [argsUsage, setArgsUsage] = useState<Map<IArgumentMetadata['name'], ArgMetadatadInUseDetails | Error>>(
        new Map(),
    );

    const isUsed = useCallback(
        (argName: string): boolean | undefined => {
            const usage = argsUsage?.get(argName);

            if (usage === undefined || usage instanceof Error) {
                return undefined;
            }

            return (
                usage.dependent_arg_names.length > 0 ||
                usage.actions.length > 0 ||
                usage.policies.length > 0 ||
                usage.templates.length > 0
            );
        },
        [argsUsage],
    );

    const [usageArgsExposed, setUsageArgsExposed] = useState<Set<string>>(new Set());
    const [usageArgsChunks, setUsageArgsChunks] = useState<string[][]>([]);
    const setUsageArgs = useCallback(
        debounce(
            (args: string[]) => {
                setUsageArgsChunks((chunks) => {
                    const totalArgs = chunks.reduce((total, chunk) => total + chunk.length, 0);
                    const newChunks = chunk(args.slice(totalArgs), 100);
                    return [...chunks, ...newChunks];
                });
            },
            500,
            { trailing: true },
        ),
        [setUsageArgsChunks],
    );
    useEffect(() => {
        setUsageArgs([...usageArgsExposed]);
    }, [usageArgsExposed.size, setUsageArgs]);

    const usageQueries = usePoliciesUsageQuery(usageArgsChunks);
    const isUsageLoading = usageQueries.some((query) => query.isLoading || query.isFetching);
    useEffect(() => {
        for (const chunkIndex in usageArgsChunks) {
            const argNames = usageArgsChunks[chunkIndex];
            const { data, error } = usageQueries[chunkIndex];
            if (error) {
                const map = argNames.reduce<Map<IArgumentMetadata['name'], Error>>((map, argName) => {
                    return map.set(argName, error);
                }, new Map());
                setArgsUsage((argsUsage) => new Map(Array.from(argsUsage.entries()).concat(Array.from(map.entries()))));
            } else if (data) {
                Object.entries(data).forEach(([argName, usage]) => {
                    usage.policies
                        .concat(usage.templates)
                        .concat(usage.actions)
                        .forEach(({ organization_id }) => {
                            const orgSet = (argsByOrgs.current.get(organization_id) || new Set()).add(argName);
                            argsByOrgs.current.set(organization_id, orgSet);
                        });
                });
                setArgsUsage((argsUsage) => new Map(Array.from(argsUsage.entries()).concat(Object.entries(data))));
            }
        }
    }, [usageQueries.filter((query) => query.data || query.error).length]);

    const UsageCell = useMemo(() => {
        return function UsageCell({ metadata }: { metadata: UiIArgumentMetadata }) {
            if (!usageArgsExposed.has(metadata.name)) {
                setTimeout(() => setUsageArgsExposed((usage) => new Set([...usage, metadata.name])));
            }
            return metadata.id ? (
                <ArgUsage
                    argName={metadata.name}
                    loading={!argsUsage.get(metadata.name)}
                    error={argsUsage.get(metadata.name) instanceof Error ? 'Error loading' : undefined}
                    data={
                        argsUsage.get(metadata.name) instanceof Error
                            ? undefined
                            : (argsUsage.get(metadata.name) as ArgMetadatadInUseDetails)
                    }
                />
            ) : null;
        };
    }, [argsUsage, usageArgsExposed]);

    const RowActions = useCallback(
        function RowActions({ metadata }: { metadata: UiIArgumentMetadata }) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const [showConfirm, setShowConfirm] = useState(false);
            const arg = argsMetadata?.find((arg) => arg.id === metadata.id);
            const argName = arg?.name;
            const canRunExperiment = arg?.extractor_name === 'llm' && arg.arg_type === 'TEXT_ARGUMENT';

            const usageStatus = argName ? isUsed(argName) : undefined;
            const isThisArgLoading = argName && usageStatus === undefined && usageArgsExposed.has(argName);
            const title =
                isThisArgLoading || argName === undefined
                    ? 'Fetching usage...'
                    : isUsed(argName)
                      ? 'Cannot delete: Arg is being used'
                      : 'Delete argument';

            return (
                <Box>
                    <IconButton title="Edit argument" onClick={() => editArgMetadata(argName)}>
                        {canRunExperiment ? <DesignServicesIcon /> : <EditIcon />}
                    </IconButton>
                    <Tooltip title={title}>
                        <span>
                            <IconButton
                                disabled={
                                    argName === undefined || isUsed(argName) || isUsageLoading || isThisArgLoading
                                }
                                onClick={() => setShowConfirm(true)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <AlertDialog
                        open={showConfirm}
                        title="Delete argument"
                        description="Are you sure you want to delete this argument?"
                        onAgree={() => {
                            const argToDelete = argsMetadata?.find((arg) => arg.id === metadata.id);
                            if (argToDelete) {
                                deleteArgMetadata.mutateAsync(argToDelete);
                            }
                        }}
                        onClose={() => setShowConfirm(false)}
                    />
                </Box>
            );
        },
        [argsMetadata, argsUsage, isUsageLoading, usageArgsExposed],
    );

    const data = useMemo<UiIArgumentMetadata[]>(() => {
        return (argsMetadata ?? [])
            .map((arg) => ({ ...arg, uiId: String(arg.id) }))
            .sort((wf1, wf2) => wf1.name.localeCompare(wf2.name));
    }, [argsMetadata]);

    const [newArg, setNewArg] = useState<IArgumentMetadata | null>(null);

    const onArgMetadataEdit = useCallback(
        (arg: IArgumentMetadata) => {
            return (
                arg.id
                    ? editArgMetadataMutation.mutateAsync(arg)
                    : createArgMetadataMutation.mutateAsync({ metadata: arg }).then(setNewArg)
            ).finally(() => {
                editArgMetadata(undefined);
            });
        },
        [data],
    );

    const user = useUser();

    const columns: MRT_ColumnDef<UiIArgumentMetadata, any>[] = useMemo(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
                filterFn: 'contains',
                size: 100,
                grow: false,
                enableResizing: false,
            },
            {
                header: 'Name',
                accessorKey: 'name',
                filterFn: 'contains',
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
                size: 240,
            },
            {
                header: 'Title',
                accessorKey: 'title',
                filterFn: 'contains',
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
                size: 360,
            },
            {
                header: 'Description',
                accessorKey: 'description',
                filterFn: 'contains',
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
                size: 360,
            },
            {
                header: 'DType',
                accessorKey: 'dtype',
                filterFn: 'contains',
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
            },
            {
                header: 'ArgType',
                accessorKey: 'arg_type',
                filterVariant: 'select',
                filterSelectOptions: [...new Set(data.map((argMd) => argMd.arg_type))],
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
            },
            {
                header: 'Extractor',
                accessorKey: 'extractor_name',
                filterVariant: 'autocomplete',
                filterSelectOptions: [...new Set(data.map((argMd) => argMd.extractor_name))],
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
            },
            {
                header: 'Options',
                accessorFn: (row) => js_beautify(JSON.stringify(row.options?.categories || {})),
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
            },
            {
                header: 'Params',
                filterFn: 'contains',
                accessorFn: (row) => (isEmpty(row.params) ? '' : js_beautify(JSON.stringify(row.params))),
                Cell: ({ cell }) => (
                    <HighlightedTooltip value={cell.getValue()} filterValue={cell.column.getFilterValue()} />
                ),
            },
            {
                header: 'Usage',
                accessorFn: (arg) => arg.name,
                filterVariant: 'multi-select',
                filterSelectOptions: allOrgs.map((org) => ({ label: org.name, value: org.id })),
                filterFn(row, _, orgIds: IOrganization['id'][]) {
                    if (!orgIds.length) return true;
                    return orgIds.some((orgId) => argsByOrgs.current.get(orgId)?.has(row.original.name));
                },
                size: 360,
                enableColumnFilter: true,
                Cell: ({ row: { original: metadata } }) => <UsageCell metadata={metadata} />,
            },
        ],
        [data, allOrgs, argsByOrgs, UsageCell],
    );

    //  = useState<Record<string, number>>({});
    const [columnSizing, setColumnSizing] = useAtom(argMetadataColumnSizingAtom);

    const table = useMaterialReactTable<UiIArgumentMetadata>({
        columns,
        data,
        enableRowActions: true,
        renderRowActions: ({ row: { original: metadata } }) => <RowActions metadata={metadata} />,
        initialState: { showColumnFilters: true },
        state: {
            isLoading: !argsMetadata || isFetching,
            columnPinning: { left: ['mrt-row-actions', 'id', 'name'] },
            density: 'compact',
            columnSizing,
        },
        onColumnSizingChange: setColumnSizing,
        enableColumnResizing: true,
        enableTopToolbar: false,
        enablePagination: false,
        enableColumnActions: false,
        enableRowVirtualization: true,
        enableBottomToolbar: false,
        enableStickyHeader: true,
        muiFilterTextFieldProps: {
            placeholder: 'Filter',
            style: {
                minWidth: 'auto',
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                size: 96,
                grow: false,
                Header: () => '',
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: '100%',
            },
        },
    });

    useEffect(() => {
        if (!newArg) return;
        const index = data.findIndex((arg) => arg.id === newArg.id);
        const container = table.refs.tableContainerRef.current;
        const tableEl = container?.querySelector('table');
        if (!container || !tableEl) return;
        container.scrollTo(0, (tableEl.clientHeight * index) / data.length);
        setNewArg(null);
    }, [newArg, data]);

    if (!user?.isAdmin) {
        return <div>Access denied</div>;
    }

    return (
        <>
            <MaterialReactTable table={table} />
            {argBeingEdited && (
                <ArgMetadataEditDialog
                    argMetadata={argBeingEdited}
                    onSubmit={onArgMetadataEdit}
                    onClose={() => editArgMetadata(undefined)}
                />
            )}
            {!isFetching && (
                <Fab
                    color="primary"
                    aria-label="add"
                    onClick={onNewArg}
                    sx={{
                        position: 'absolute',
                        bottom: theme.spacing(4),
                        right: theme.spacing(4),
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </>
    );
});

ArgumentMetadataEditor.displayName = 'ArgumentMetadataEditor';
